import { cx } from '../cx';

/**
 * THE SCROLL EDGE EFFECT, AS THE KIT DRAWS IT (wave 4 round 2, C2-6, C2-20, C2-21).
 *
 * The `Kit` page ships three masters and no more: `Scroll Edge Effect/Dark - Top - Hard`,
 * `Scroll Edge Effect/Light - Top - Hard` and `Scroll Edge Effect/Top - Soft (no text)`.
 * Each is TWO layers, not a ramp of bands:
 *
 * - HARD: a rect `Color`, flat, dark `#1E1E1E` at layer opacity 0.55 and light `#FFFFFF`
 *   at 0.85, over a rect `Blur` carrying a background blur of radius 6 with an INSIDE
 *   border `#FFFFFF` alpha 0.10 (light `#000000` alpha 0.05) at thickness 0.6666 - the
 *   hairline at the guarded edge. That is what "a more opaque, clearly defined linear
 *   boundary" means (hig:
 *   https://developer.apple.com/design/human-interface-guidelines/scroll-views).
 * - SOFT: a rect `Gradient Mask`, white to transparent at layer opacity 0.60, over a rect
 *   `Blur` of radius 100. No colour of its own at all.
 *
 * AND IT IS BOUNDED BY ITS HOST. Apple instances it at exactly the bar's height and only
 * over the pane that scrolls: in `Windows/Dark/Unified Toolbar + Title + Sidebar/Active`
 * the instance sits at (0,0,584,52) inside the `Right Pane`, under the toolbar group and
 * over the scroll view. Zero overhang. Ours hung 28px past its host and stacked five
 * hand-made blur bands, which is why the strip under the toolbar was blurred before
 * anything scrolled (C2-21) and why the capsules had nothing sharp left to bend.
 *
 * ONE PER VIEW, AND NEVER TWO STYLES IN ONE ("Apply one scroll edge effect per view",
 * hig above; "you should avoid mixing or stacking them", wwdc:
 * https://developer.apple.com/videos/play/wwdc2025/356/ at 11:50).
 */
export type ScrollEdgeStyle = 'soft' | 'hard';

/**
 * WHAT THE HOST SITS ON. The hard style paints a tint, and a tint's whole job is to be the
 * ground the content dissolves into, so it has to BE that ground: the window (the kit's own
 * master), the chrome of a sidebar or a menu, or a settings group box, whose fill the Forms
 * master gives (`Forms/Example Forms/{Light,Dark}`, the `Form` group at 3 percent). The
 * soft style carries no colour in the kit and takes none here, so `ground` changes nothing
 * for it - which is stated rather than silently ignored.
 */
export type ScrollEdgeGround = 'canvas' | 'chrome' | 'group';

export interface ScrollEdgeProps {
  /** Which side of the scrolling content the host guards. */
  readonly edge?: 'top' | 'bottom' | undefined;
  /**
   * Apple picks by what stands in the bar: "Soft is the default and the one you'll use in
   * most cases, especially on iOS and iPadOS ... Hard is mostly used on macOS. It creates a
   * stronger, more opaque boundary, ideal for interactive text, controls without
   * backgrounds, or pinned table headers that need extra clarity" (wwdc: 356 at 11:18).
   */
  readonly style?: ScrollEdgeStyle | undefined;
  readonly ground?: ScrollEdgeGround | undefined;
  /**
   * THE NAME THE REVIEW ENUMERATES THIS HOST BY, and the reason the prop is required. Check
   * (u) in `packages/e2e/review/behaviour.spec.ts` asks the running product which fixed
   * edges carry an effect and names the ones that do not; a host with no name can only be
   * reported as a selector nobody can look up. A slice mounting the effect on a new host
   * adds the name to that check's list in the same round.
   */
  readonly host: string;
}

/**
 * The host must be positioned and `isolate` so the effect paints behind the host's own
 * content and over what scrolls beneath it, and the effect fills the host exactly: its box
 * IS the bar's box, which is what check (t) asserts.
 */
export function ScrollEdge({
  edge = 'top',
  ground = 'canvas',
  style = 'soft',
  host,
}: ScrollEdgeProps) {
  return (
    <div
      aria-hidden
      data-scroll-edge={host}
      data-scroll-edge-style={style}
      className={cx(
        'scroll-edge',
        `scroll-edge-${style}`,
        edge === 'bottom' && 'scroll-edge-bottom',
        ground !== 'canvas' && `scroll-edge-${ground}`,
      )}
    >
      {/* The master's `Blur` layer. The `Color` layer and the hairline are its pseudo
          elements, so one element carries what the kit draws in two and no host pays for
          a second backdrop filter. */}
      <div />
    </div>
  );
}
